import type { LocalTime } from '@zg-rentals/ts-z-calendar-client';
import { TourDuration } from '@zg-rentals/ts-z-calendar-client';
import { differenceInMinutes } from 'date-fns';

import { convertLocalTimeToDate } from './formatTime';
import type { ZCalendarEvent } from '../constants';
import { NUMBER_TO_TOUR_DURATION_MAP, TOUR_DURATION_NUMBER_MAP } from '../constants';

export const getMinCalendarStartTime = (values: Array<number>, calendarStartTime: LocalTime) => {
  const minEventHour = Math.min(...values);
  const minLocalTime =
    minEventHour >= calendarStartTime.hour ? calendarStartTime : { hour: minEventHour, minute: 0, second: 0, nano: 0 };
  return convertLocalTimeToDate(minLocalTime);
};

export const getMaxCalendarEndTime = (values: Array<number>, calendarEndTime: LocalTime) => {
  const maxEventHour = Math.max(...values);

  if (maxEventHour === 24) {
    return undefined;
  }

  const maxLocalTime =
    maxEventHour < calendarEndTime.hour ? calendarEndTime : { hour: maxEventHour, minute: 0, second: 0, nano: 0 };
  return convertLocalTimeToDate(maxLocalTime);
};

// Step is the number of minutes for each timeslot
export const getCalendarStepByTourDuration = (tourDuration: TourDuration) => {
  if (tourDuration === TourDuration.MINUTES_60) {
    return TOUR_DURATION_NUMBER_MAP[TourDuration.MINUTES_30];
  }

  return TOUR_DURATION_NUMBER_MAP[tourDuration];
};

// The `step` and `timeslots` values are used to determine the time interval for a timeslot group
// step * timeslots = time interval for a timeslot group
// Examples:
// TourDuration.MINUTES_15: step = 15, timeslots = 2 -> 30 minute timeslot group
// TourDuration.MINUTES_20: step = 20, timeslots = 3 -> 60 minute timeslot group
// TourDuration.MINUTES_30: step = 30, timeslots = 2 -> 60 minute timeslot group
// TourDuration.MINUTES_60: step = 30, timeslots = 2 -> 60 minute timeslot group
//
// NOTE: This impacts the time gutter labels. A new time label is displayed for each timeslot group.
// When step = 15, we set timeslots = 2 to display 30 minute time label intervals (12:00am, 12:30am instead of 12am, 1am with 60 min timeslot groups)
export const getNumTimeSlotsPerGroupByTourDuration = (tourDuration: TourDuration) => {
  if (tourDuration === TourDuration.MINUTES_20) {
    return 3;
  }

  return 2;
};

export const getMinEventDuration = (
  allCalendarEvents: Array<ZCalendarEvent>,
  listingConfigTourDuration: TourDuration,
): TourDuration => {
  const eventDurations = allCalendarEvents.map((event) => differenceInMinutes(event.end, event.start));
  const minEventDurationFromEvents = Math.min(...eventDurations);
  // Listing config duration should be used if it is shorter than the shortest duration from events
  const minEventDuration = Math.min(minEventDurationFromEvents, TOUR_DURATION_NUMBER_MAP[listingConfigTourDuration]);

  if (minEventDuration in NUMBER_TO_TOUR_DURATION_MAP) {
    return NUMBER_TO_TOUR_DURATION_MAP[minEventDuration as keyof typeof NUMBER_TO_TOUR_DURATION_MAP];
  }

  return TourDuration.MINUTES_15;
};
